import moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SubSink } from 'subsink';

import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';

import { PermissionUtils } from '@mitsubishi/core';
import {
  News,
  NewsAdminFacade,
  NewsService,
  NewsStatusEnum,
  NewsStatuses,
  NewsTagContextEnum,
} from '@mitsubishi/news/data-access';
import { ApmDateUtils } from '@mitsubishi/utils';

export type NewsAdmingDialogData = {
  newsId?: string;
  news?: News;
};

@Component({
  selector: 'apm-news-admin-dialog',
  templateUrl: './news-admin-dialog.component.html',
  styleUrls: ['./news-admin-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NewsAdminDialogComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper!: MatStepper;

  canConvertToArticle = PermissionUtils.hasMenageArticlesPermission();

  news?: News;

  form!: UntypedFormGroup;
  isNews$ = this.newsAdminFacade.isNews$;

  statuses = NewsStatusEnum;
  statuses$: Observable<NewsStatuses[]> = this.newsAdminFacade.statuses$;

  private subs: SubSink = new SubSink();

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: NewsAdmingDialogData | null,
    public dialogRef: MatDialogRef<NewsAdminDialogComponent>,
    private newsService: NewsService,
    private newsAdminFacade: NewsAdminFacade,
    private router: Router
  ) {}

  get fc() {
    return this.form.controls;
  }

  get fv() {
    return this.form.getRawValue();
  }

  ngOnInit() {
    this.createForm();
    if (this.dialogData?.newsId) {
      this.setNewsData();
    }
    if (this.dialogData?.news) {
      this.fc.creation.patchValue({
        title: this.dialogData.news.title,
        content: this.dialogData.news.content,
      });
    }
    this.newsAdminFacade.newsCreatedSuccessfully$.subscribe(() => {
      this.dialogRef.close();
    });
  }

  setNewsData() {
    if (!this.dialogData?.newsId) return;

    this.subs.sink = this.newsService.getNewsDetail(this.dialogData.newsId).subscribe(news => {
      this.news = news;

      const freeTags = news.tags.filter(tag => tag.context === NewsTagContextEnum.Free || tag.context === null);
      this.fc.creation.patchValue({
        title: this.news.title,
        status: this.news.status,
        image: { name: this.news.coverOriginalFilename },
        publishDate: this.news.publishDate ? ApmDateUtils.getUtcLocalDate(this.news.publishDate) : null,
        expiringDate: this.news.expiringDate ? ApmDateUtils.getUtcLocalDate(this.news.expiringDate) : null,
        tags: freeTags,
        content: this.news.content,
      });

      if (news.status !== NewsStatusEnum.PUBLISHED) {
        this.statuses$ = this.statuses$.pipe(
          map(statuses => statuses.filter(status => status.code !== NewsStatusEnum.PUBLISHED))
        );
        this.toggleStatusFields(true);
      } else {
        this.statuses$ = this.newsAdminFacade.statuses$;
        this.toggleStatusFields(false);
      }

      this.fc.visibility.patchValue({
        countries: this.news.countries ?? [],
        organizationTypeCodes: this.news.organizationTypeCodes ?? [],
        organizationIds: this.news.organizationIds ?? [],
        specificUsers: this.news.specificUsers ?? [],
      });

      const classTags = news.tags.filter(tag => tag.context === NewsTagContextEnum.Class);
      const subClassTags = news.tags.filter(tag => tag.context === NewsTagContextEnum.Subclass);
      const subFamilyTags = news.tags.filter(tag => tag.context === NewsTagContextEnum.Subfamily);
      const modelTags = news.tags.filter(tag => tag.context === NewsTagContextEnum.Model);
      this.fc.tags.patchValue({
        classes: classTags ?? [],
        subClasses: subClassTags ?? [],
        subFamilies: subFamilyTags ?? [],
        models: modelTags ?? [],
      });
    });
  }

  toggleStatusFields(enabled: boolean) {
    const creationForm = this.fc.creation as FormGroup;

    if (enabled) {
      creationForm.controls.status.enable();
      creationForm.controls.publishDate.enable();
    } else {
      creationForm.controls.status.disable();
      creationForm.controls.publishDate.disable();
    }
  }

  createForm() {
    this.form = new UntypedFormGroup({
      creation: new UntypedFormGroup({
        title: new UntypedFormControl('', [Validators.required]),
        status: new UntypedFormControl({ value: 'DRAFT', disabled: !this.dialogData?.newsId }, Validators.required),
        image: new UntypedFormControl('', [Validators.required]),
        publishDate: new UntypedFormControl(null, [Validators.required]),
        expiringDate: new UntypedFormControl(null, [Validators.required]),
        tags: new UntypedFormControl([]),
        content: new UntypedFormControl('', [Validators.required]),
      }),
      visibility: new UntypedFormGroup({
        countries: new UntypedFormControl([]),
        organizationTypeCodes: new UntypedFormControl([]),
        organizationIds: new UntypedFormControl([]),
        specificUsers: new UntypedFormControl([]),
      }),
      tags: new UntypedFormGroup({
        classes: new UntypedFormControl([]),
        subClasses: new UntypedFormControl([]),
        subFamilies: new UntypedFormControl([]),
        models: new UntypedFormControl([]),
      }),
    });
  }

  goNext() {
    if (this.stepper.selected) {
      this.stepper.selected.completed = true;
    }
    this.stepper.next();
  }

  closeDialog() {
    this.dialogRef.close();
  }

  createNews() {
    if (this.dialogData?.newsId) {
      this.newsAdminFacade.updateNewsAdmin(this.dialogData.newsId, this.fv);
    } else {
      this.newsAdminFacade.createNewsAdmin(this.fv);
    }
  }

  convertToArticle() {
    this.dialogData = { newsId: '' };
    this.newsAdminFacade.setNewsAdminType(1);

    if (this.news && this.news.coverFilename) {
      if (!this.fv.creation.image.size)
        // image has not changed before converting to article
        this.newsService.getNewsImageUrl(this.news.coverFilename).subscribe(async imageUrl => {
          const response = await fetch(imageUrl);
          const data = await response.blob();

          const file = new File([data], this.fv.creation.image.name, {
            type: this.news?.contentType ?? data.type,
          });

          this.fc.creation.patchValue({
            image: file,
          });
        });
    } else {
      this.fc.creation.patchValue({
        image: '',
      });
    }

    this.fc.creation.patchValue({
      status: NewsStatusEnum.DRAFT,
    });

    this.toggleStatusFields(true);

    this.router.navigate(['library', 'articles-admin']);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
